import { create } from 'zustand';
import { useAuthStore } from '@/stores/AuthStore';
import { BELeague, BELeagueEntry } from '@/lib/leagueMappers';
import {
  fetchLeagueDetail as fetchLeagueDetailApi,
  fetchLeagueHistory,
  fetchLeagueEntries,
  fetchLeaderboard,
  fetchLeaderboardPeriods,
  updateLeague as updateLeagueApi,
  regenerateLeague as regenerateLeagueApi,
  removeLeagueEntry,
  fetchCurrentGameweek,
  getErrorMessage,
} from '@/lib/network';
import type {
  LeaderboardData,
  LeaderboardEntry,
  LeaderboardPeriod,
  LeagueHistoryItem,
} from '@/lib/network';
import type { UpdateLeaguePayload } from '@/lib/network';
import { getUserSessionGeneration } from '@/lib/userSessionGeneration';

export interface NextDeadline {
  gameweek: number;
  deadline: Date;
}

export const LEAGUE_OVERALL_PERIOD_VALUE = 'overall';

/**
 * League entry enriched with the per-team figures from the leaderboard
 * endpoint. `period_points` is the points scored in the selected
 * gameweek/matchday (or the current one when Overall is selected); both
 * stay undefined when the team is missing from the leaderboard response.
 */
export interface LeagueStandingEntry extends BELeagueEntry {
  average_points?: number;
  period_points?: number;
}

interface LeagueDetailState {
  league: BELeague | null;
  history: LeagueHistoryItem[];
  entries: LeagueStandingEntry[];
  myEntry: LeagueStandingEntry | null;
  nextDeadline: NextDeadline | null;
  leaderboardPeriodType: 'matchday' | 'gameweek';
  leaderboardPeriods: LeaderboardPeriod[];
  leaderboardLastUpdated: string | null;
  loading: boolean;
  updating: boolean;
  regenerating: boolean;
  removingEntryId: string | null;
  error: string | null;
  fetchLeagueDetail: (leagueId: string, selectedPeriodId?: string) => Promise<void>;
  updateLeague: (id: string, data: UpdateLeaguePayload) => Promise<void>;
  regenerateLeague: (leagueId: string, startGameweek: number) => Promise<string>;
  removeEntry: (leagueId: string, entryId: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  league: null as BELeague | null,
  history: [] as LeagueHistoryItem[],
  entries: [] as LeagueStandingEntry[],
  myEntry: null as LeagueStandingEntry | null,
  nextDeadline: null as NextDeadline | null,
  leaderboardPeriodType: 'gameweek' as const,
  leaderboardPeriods: [] as LeaderboardPeriod[],
  leaderboardLastUpdated: null as string | null,
  loading: false,
  updating: false,
  regenerating: false,
  removingEntryId: null as string | null,
  error: null as string | null,
};

function applyLeaderboardEntries(
  entries: BELeagueEntry[],
  leaderboardEntries: LeaderboardEntry[]
): LeagueStandingEntry[] {
  if (leaderboardEntries.length === 0) {
    return [...entries].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
  }

  const leaderboardByTeamId = new Map(leaderboardEntries.map(entry => [entry.team_id, entry]));

  return entries
    .map<LeagueStandingEntry>(entry => {
      const leaderboardEntry = leaderboardByTeamId.get(entry.fantasy_team_id);
      if (!leaderboardEntry) return entry;

      return {
        ...entry,
        rank: leaderboardEntry.team_rank,
        points: String(Math.round(leaderboardEntry.total_points)),
        average_points: leaderboardEntry.average_points,
        period_points: leaderboardEntry.gameweek_points ?? leaderboardEntry.matchday_points,
        user: entry.user
          ? {
              ...entry.user,
              username: leaderboardEntry.user_display_name ?? entry.user.username,
            }
          : entry.user,
        fantasy_team: entry.fantasy_team
          ? {
              ...entry.fantasy_team,
              team_name: leaderboardEntry.team_name,
            }
          : entry.fantasy_team,
      };
    })
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity));
}

export const useLeagueDetailStore = create<LeagueDetailState>(set => ({
  ...initialState,

  fetchLeagueDetail: async (leagueId: string, selectedPeriodId = LEAGUE_OVERALL_PERIOD_VALUE) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ loading: true, error: null });
    try {
      const [leagueRes, entriesRes, historyRes] = await Promise.all([
        fetchLeagueDetailApi(leagueId),
        fetchLeagueEntries(leagueId),
        // Tournament history is auxiliary — the page still works without it
        fetchLeagueHistory(leagueId).catch((): null => null),
      ]);

      const league = leagueRes.data;
      const gameweekWindowRes = league.tournament_calendar_id
        ? await fetchCurrentGameweek({
            tournamentCalendarId: league.tournament_calendar_id,
          }).catch((): null => null)
        : null;
      let periods: LeaderboardPeriod[] = [];
      // Gameweek is the default axis; only a knockout calendar reports 'matchday'.
      let periodType: 'matchday' | 'gameweek' = 'gameweek';
      let leaderboard: LeaderboardData | null = null;

      if (league.tournament_calendar_id) {
        const periodsRes = await fetchLeaderboardPeriods(league.tournament_calendar_id).catch(
          (): null => null
        );
        periods = periodsRes?.data.periods ?? [];
        periodType = periodsRes?.data.type ?? 'gameweek';

        const leaderboardParams = {
          tournament_calendar: league.tournament_calendar_id,
          type: periodType,
          league_id: leagueId,
          limit: 100,
          ...(selectedPeriodId !== LEAGUE_OVERALL_PERIOD_VALUE && periodType === 'matchday'
            ? { upto_matchday_id: selectedPeriodId }
            : {}),
          ...(selectedPeriodId !== LEAGUE_OVERALL_PERIOD_VALUE && periodType === 'gameweek'
            ? { upto_gameweek_id: selectedPeriodId }
            : {}),
        };

        const leaderboardRes = await fetchLeaderboard(leaderboardParams).catch((): null => null);
        leaderboard = leaderboardRes?.data ?? null;
      }

      const entries = entriesRes.data.items;
      const sorted = applyLeaderboardEntries(entries, leaderboard?.entries ?? []);
      const userId = useAuthStore.getState().userId;
      const myEntry = userId ? (sorted.find(e => e.user_id === userId) ?? null) : null;

      let nextDeadline: NextDeadline | null = null;
      const deadlineGameweeks = [
        gameweekWindowRes?.data.currentGameweek,
        gameweekWindowRes?.data.nextGameweek,
      ];
      for (const gameweek of deadlineGameweeks) {
        if (!gameweek?.deadlineAt || !['open', 'upcoming'].includes(gameweek.status)) continue;
        const deadline = new Date(gameweek.deadlineAt);
        if (Number.isNaN(deadline.getTime())) continue;
        nextDeadline = { gameweek: gameweek.number, deadline };
        break;
      }

      if (sessionGeneration !== getUserSessionGeneration()) return;

      set({
        league,
        history: historyRes?.data ?? [],
        entries: sorted,
        myEntry,
        nextDeadline,
        leaderboardPeriodType: periodType,
        leaderboardPeriods: periods,
        leaderboardLastUpdated: leaderboard?.last_updated ?? null,
        loading: false,
      });
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) return;
      set({ error: getErrorMessage(e, 'Failed to fetch league detail'), loading: false });
    }
  },

  updateLeague: async (id: string, data: UpdateLeaguePayload) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ updating: true });
    try {
      const res = await updateLeagueApi(id, data);
      if (sessionGeneration !== getUserSessionGeneration()) return;
      set({ league: res.data, updating: false });
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ updating: false });
      throw e;
    }
  },

  regenerateLeague: async (leagueId: string, startGameweek: number) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ regenerating: true });
    try {
      const res = await regenerateLeagueApi(leagueId, startGameweek);
      if (sessionGeneration !== getUserSessionGeneration()) {
        throw new Error('User session changed while regenerating the league');
      }
      set({ regenerating: false });
      return res.data.id;
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ regenerating: false });
      throw e;
    }
  },

  removeEntry: async (leagueId: string, entryId: string) => {
    const sessionGeneration = getUserSessionGeneration();
    set({ removingEntryId: entryId });
    try {
      await removeLeagueEntry(leagueId, entryId);
      if (sessionGeneration !== getUserSessionGeneration()) return;
      set(state => ({
        entries: state.entries.filter(e => e.id !== entryId),
        removingEntryId: null,
      }));
    } catch (e) {
      if (sessionGeneration !== getUserSessionGeneration()) throw e;
      set({ removingEntryId: null });
      throw e;
    }
  },

  reset: () => set(initialState),
}));
